import React, { useMemo, useState } from 'react'
import Loader from '../../../atoms/Loader'
import Typography from '../../../atoms/Typography'
import FullScreenButton from '../../../atoms/buttons/FullScreenButton'
import SandboxedFrame from '../../../molecules/SandboxedFrame'
import FullscreenPreviewDialog from '../../dialogs/FullscreenPreviewDialog'
import { downloadBlob } from '../../../../enviroment/utils/downloadBlob'
import { deriveHtmlFileName } from '../../../../enviroment/utils/deriveHtmlFileName'
import { toOfflineHtml } from '../../../../enviroment/utils/toOfflineHtml'
import { wrapInNavigationGuard } from '../../../../enviroment/utils/wrapInNavigationGuard'
import { cn } from '../../../../enviroment/lib/utils'
import { formatFileSize } from '../../../../enviroment/lib/formatters/format-file-size'

const REPORT_HEIGHT = 'h-[420px]'

// Every srcDoc change reloads a frame, so while the document streams only this placeholder is shown.
export const HtmlReportLoader: React.FC = () => (
  <div
    className={cn(
      'my-2 flex flex-col items-center justify-center gap-3 rounded-lg border border-gray-700',
      REPORT_HEIGHT,
    )}
  >
    <Loader className="text-light-green-500" />
    <Typography variant="sm" className="text-gray-200">
      Generating report...
    </Typography>
  </div>
)

const HtmlReportMessage: React.FC<{ html: string }> = ({ html }) => {
  const [isFullscreen, setIsFullscreen] = useState(false)
  const report = useMemo(() => {
    const offlineHtml = toOfflineHtml(html)
    return {
      frameHtml: wrapInNavigationGuard(offlineHtml),
      file: new Blob([offlineHtml], { type: 'text/html' }),
      fileName: deriveHtmlFileName(html, new Date()),
    }
  }, [html])

  return (
    <div className="relative my-2">
      <div className={cn('overflow-hidden rounded-lg border border-gray-700', REPORT_HEIGHT)}>
        <SandboxedFrame srcDoc={report.frameHtml} title={report.fileName} />
      </div>
      <FullScreenButton
        onClick={() => setIsFullscreen(true)}
        className="absolute bottom-3 right-5 bg-gray-900/90 px-3 py-1.5"
      />

      {isFullscreen && (
        <FullscreenPreviewDialog
          isOpen={isFullscreen}
          onClose={() => setIsFullscreen(false)}
          fileName={report.fileName}
          details={['Generated by COPA', formatFileSize(report.file.size)]}
          onDownload={() => downloadBlob(report.file, report.fileName)}
        >
          <SandboxedFrame srcDoc={report.frameHtml} title={report.fileName} />
        </FullscreenPreviewDialog>
      )}
    </div>
  )
}

export default HtmlReportMessage
